import { BillInterval, FeatureName, ModuleName, PricingTier, ProductDescription } from "./product";
import { PaymentDetailsResponse, PaymentType } from "@/payment/Payment";
import { EnterpriseAccountInfo } from "@/types/CompanyInfo";
import { EnterpriseAccount, ProductName } from "./common";

export enum SubscriptionStatus {
  NOT_IN_USE_YET = "NOT_IN_USE_YET",
  CREATED_INACTIVE = "CREATED_INACTIVE",
  PAYMENT_REGISTERED = "PAYMENT_REGISTERED",
  ACTIVE = "ACTIVE",
}

export enum SubscriptionType {
  FREE = "FREE",
  PRO = "PRO",
  BASIC = "BASIC",
  SHAREHOLDER_REGISTER_LEGACY = "SHAREHOLDER_REGISTER_LEGACY",
}

export interface PaidSubscription {
  organisationNumber: string;
  subscriptionStatus: SubscriptionStatus;
  subscriptionType: SubscriptionType;
  productName?: string;
  isActive: boolean;
  allowsDbot?: boolean;
  allowsArchive?: boolean;
  allowsOrdinaryGeneralMeeting?: boolean;
  allowsMeeting?: boolean;
  modules?: ModuleName[];
  features: FeatureName[];

  //overview
  allowDashboard: boolean;
  allowEntityData: boolean;
  allowArticles: boolean;
  allowPoAs: boolean;
  allowOwnership: boolean;
  allowCompanyRoles: boolean;
  allowCompanySettings: boolean;

  //shareholder register
  allowShareholderRegisterDashboard: boolean;
  allowShareholders: boolean;
  allowTransactions: boolean;
  allowShareClasses: boolean;
  allowFinancialInstruments: boolean;
  allowCapTable: boolean;
  allowCommitments: boolean;
  allowShareholderRegisterStatement: boolean;
  allowBeneficialOwners: boolean;

  //ownership register
  allowOwners: boolean;
  allowOwnersTransactions: boolean;
  allowOwnersShareClasses: boolean;

  //archive
  allowBoardArchive: boolean;
  allowShareholderArchive: boolean;
  allowAdminArchive: boolean;
  allowCustomArchives: boolean;

  //messaging
  allowBoardMessages: boolean;
  allowShareholderMessages: boolean;
}

export const subscriptionFeatures = (features: FeatureName[]) => ({
  features,
  allowDashboard: features.includes(FeatureName.dashboard),
  allowEntityData: features.includes(FeatureName.entityData),
  allowArticles: features.includes(FeatureName.articles),
  allowPoAs: features.includes(FeatureName.poas),
  allowOwnership: features.includes(FeatureName.ownership),
  allowCompanyRoles: features.includes(FeatureName.companyRoles),
  allowCompanySettings: features.includes(FeatureName.companySettings),
  allowShareholderRegisterDashboard: features.includes(FeatureName.shareholderRegisterDashboard),
  allowShareholders: features.includes(FeatureName.shareholders),
  allowTransactions: features.includes(FeatureName.transactions),
  allowShareClasses: features.includes(FeatureName.shareClasses),
  allowFinancialInstruments: features.includes(FeatureName.financialInstruments),
  allowCapTable: features.includes(FeatureName.capTable),
  allowCommitments: features.includes(FeatureName.commitments),
  allowShareholderRegisterStatement: features.includes(FeatureName.shareholderRegisterStatement),
  allowBeneficialOwners: features.includes(FeatureName.beneficialOwners),
  allowOwners: features.includes(FeatureName.owners),
  allowOwnersTransactions: features.includes(FeatureName.ownersTransactions),
  allowOwnersShareClasses: features.includes(FeatureName.ownersShareClasses),
  allowBoardArchive: features.includes(FeatureName.boardArchive),
  allowShareholderArchive: features.includes(FeatureName.shareholderArchive),
  allowAdminArchive: features.includes(FeatureName.adminArchive),
  allowCustomArchives: features.includes(FeatureName.customArchives),
  allowBoardMessages: features.includes(FeatureName.boardMessages),
  allowShareholderMessages: features.includes(FeatureName.shareholderMessages),
});

export const allFeaturesIncluded = subscriptionFeatures(Object.values(FeatureName));

export type SubscriptionStatusContext = {
  subscriptionType?: SubscriptionType;
  currentPrice?: number;
  billInterval?: BillInterval;
  companyName: string;
  orgNo: string;
  email: string;
  name: string;
  productDescription?: ProductDescription;
  productName?: ProductName;
  tier?: PricingTier;
  paymentMethod?: PaymentType;
  paymentDetails?: PaymentDetailsResponse;
  enterpriseAccountInfo?: EnterpriseAccountInfo;
  availableEnterpriseAccounts: EnterpriseAccount[];
};
